/**
 * Agent scheduler — makes a deployed agent's `cadence` actually fire.
 *
 * Until now `cadence` ('continuous' | 'hourly' | 'daily' | 'manual') was
 * decorative: it picked an icon and a label, but nothing ever read it, so a
 * deployed agent only ran when a human clicked "Run now". This closes that
 * gap with the same self-rescheduling loop the ticket autopilot uses:
 *
 *   • while the master switch is on, a background loop wakes every
 *     `intervalMs`, lists the deployed agents, and asks `dueAt()` which ones
 *     are overdue for a run given their cadence + `lastRunAt`;
 *   • it fires exactly ONE due agent per tick (most-overdue first), via the
 *     same `runAgentOnce` the manual button calls — so every scheduled run
 *     lands on the agent's append-only timeline indistinguishably from a
 *     hand-triggered one, and the queue animates rather than draining at once;
 *   • the loop self-schedules so ticks never overlap, and a per-run cancel
 *     flag keeps exactly one loop alive under React StrictMode's
 *     mount→cleanup→mount.
 *
 * Only `state === 'running'` agents are eligible — pausing an agent or
 * flipping the master switch off stops it. The switch is persisted so
 * autonomy survives a reload. No network: `runAgentOnce` already routes real
 * work through the hosted worker when configured, else simulates.
 */

import { useEffect, useState } from 'react';
import { listAgents, runAgentOnce } from '../db/agents';
import { advancePausedRuns } from './orchestrator';
import type { AgentCadence, DeployedAgent } from '../db/types';

/**
 * Cadence → minimum gap between auto-runs, in milliseconds. `manual` is
 * intentionally absent (never auto-runs). `continuous` is throttled to a
 * visible-but-not-spammy cadence so a demo session shows runs accumulate
 * without hammering the store.
 */
const CADENCE_MS: Partial<Record<AgentCadence, number>> = {
  continuous: 30_000, // ~every 30s
  hourly: 60 * 60_000, // 1h
  daily: 24 * 60 * 60_000, // 24h
};

/**
 * The timestamp (ms epoch) at which `agent` next becomes eligible to run, or
 * null if it never auto-runs (manual cadence, or not currently running). An
 * agent that has never run is due immediately.
 */
function dueAtMs(agent: DeployedAgent): number | null {
  if (agent.state !== 'running') return null;
  const gap = CADENCE_MS[agent.cadence];
  if (gap == null) return null; // manual
  const last = agent.lastRunAt ? new Date(agent.lastRunAt).getTime() : 0;
  return last + gap;
}

/** True when `agent` is overdue for an automatic run at `now`. */
export function shouldRunNow(agent: DeployedAgent, now = Date.now()): boolean {
  const due = dueAtMs(agent);
  return due != null && now >= due;
}

/**
 * ISO timestamp of the agent's next scheduled run, or null when it doesn't
 * auto-run. Powers the "next run" badge in the Agents tab. If the agent is
 * already overdue the returned time is in the past, which the UI renders as
 * "due now".
 */
export function nextRunAt(agent: DeployedAgent): string | null {
  const due = dueAtMs(agent);
  return due == null ? null : new Date(due).toISOString();
}

/** Human-friendly "next run" label for a deployed agent. */
export function fmtNextRun(agent: DeployedAgent, now = Date.now()): string {
  if (agent.state !== 'running') return agent.state === 'paused' ? 'Paused' : 'Idle';
  if (CADENCE_MS[agent.cadence] == null) return 'On demand';
  const due = dueAtMs(agent);
  if (due == null) return 'On demand';
  const diff = due - now;
  if (diff <= 0) return 'Due now';
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'in <1m';
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  const days = Math.round(hrs / 24);
  return `in ${days}d`;
}

/** A scheduled run the loop just fired, for the status line / ticker. */
export interface SchedulerEvent {
  agentId: string;
  agentName: string;
  cadence: AgentCadence;
  /** Past-tense summary from the run's newest timeline entry. */
  text: string;
  at: string;
}

const STORAGE_KEY = 'argo-os:agent-scheduler';

/** Read the persisted master-switch flag (off by default). */
export function readSchedulerEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

function writeSchedulerEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    /* ignore */
  }
}

/**
 * One scheduler tick: find the most-overdue running agent and run it. Returns
 * the event it produced, or null when nothing was due. Exported so it can be
 * unit-tested and driven manually if needed.
 *
 * NOTE: If this scheduler begins producing AgentAction proposals (rather than
 * executing them directly), wire queueApproval from src/lib/agent/approvals.ts
 * here to queue actions for operator review instead of auto-executing them.
 */
export async function schedulerTick(now = Date.now()): Promise<SchedulerEvent | null> {
  // Sweep Orchestrator runs forward first: a run whose gated approval was
  // resolved resumes on the very next tick, so autonomy doesn't depend on
  // the Goals UI being open. Best-effort — a bad sweep must not stop agents.
  try {
    await advancePausedRuns();
  } catch {
    /* swallow — the sweep is best-effort */
  }

  const agents = await listAgents();
  const due = agents
    .filter((a) => shouldRunNow(a, now))
    // Most-overdue first: an agent that has never run (lastRunAt null) sorts
    // ahead of one that ran recently.
    .sort((a, b) => {
      const at = a.lastRunAt ? new Date(a.lastRunAt).getTime() : 0;
      const bt = b.lastRunAt ? new Date(b.lastRunAt).getTime() : 0;
      return at - bt;
    });

  const next = due[0];
  if (!next) return null;

  const ran = await runAgentOnce(next.id);
  const newest = ran.timeline[ran.timeline.length - 1];
  return {
    agentId: ran.id,
    agentName: ran.name,
    cadence: ran.cadence,
    text: newest?.text ?? `${ran.name} ran`,
    at: ran.lastRunAt ?? new Date().toISOString(),
  };
}

export interface UseAgentScheduler {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  toggle: () => void;
  /** Most recent scheduled run, for a status line. */
  lastEvent: SchedulerEvent | null;
  /** Rolling window of recent scheduled runs, newest-first. */
  recentEvents: SchedulerEvent[];
  /** True while a tick is mid-flight. */
  busy: boolean;
}

/**
 * Drive the scheduler loop. While enabled, runs `schedulerTick` every
 * `intervalMs`; the loop self-schedules so ticks never overlap. The enabled
 * flag is persisted so it survives reloads.
 */
export function useAgentScheduler(intervalMs = 5000): UseAgentScheduler {
  const [enabled, setEnabledState] = useState<boolean>(() => readSchedulerEnabled());
  const [lastEvent, setLastEvent] = useState<SchedulerEvent | null>(null);
  const [recentEvents, setRecentEvents] = useState<SchedulerEvent[]>([]);
  const [busy, setBusy] = useState(false);

  const setEnabled = (on: boolean) => {
    writeSchedulerEnabled(on);
    setEnabledState(on);
  };
  const toggle = () => setEnabled(!enabled);

  useEffect(() => {
    if (!enabled) return;
    // Per-effect-run cancel flag (NOT a shared ref): under StrictMode the
    // effect mounts → cleans up → mounts again, and a shared ref would be
    // reset by the second mount, leaving two timer chains racing. A
    // closure-local flag keeps each run's cleanup authoritative.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const loop = async () => {
      if (cancelled) return;
      setBusy(true);
      try {
        const ev = await schedulerTick();
        if (!cancelled && ev) {
          setLastEvent(ev);
          setRecentEvents((prev) => [ev, ...prev].slice(0, 6));
        }
      } catch {
        /* swallow — a bad tick shouldn't kill the loop */
      } finally {
        if (!cancelled) setBusy(false);
      }
      if (!cancelled) timer = setTimeout(loop, intervalMs);
    };

    loop();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, intervalMs]);

  return { enabled, setEnabled, toggle, lastEvent, recentEvents, busy };
}
